import React from 'react';
import { Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '@/components/common/BaseDialog';

interface SessionExpiringDialogProps {
  open: boolean;
  /** Ms remaining until idle-expiry (null renders no countdown). */
  remainingMs: number | null;
  /** Extend the session (fires a keep-alive API call). */
  onStay: () => void;
  /** Log out now. */
  onLogout: () => void;
  /** True while the keep-alive request is in flight. */
  staying?: boolean;
}

/** Format ms as m:ss (floored, never negative). */
const formatRemaining = (ms: number | null): string => {
  if (ms == null) return '';
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/**
 * Proactive "your session is about to expire" reminder. Distinct from
 * SessionExpiredDialog (which fires AFTER the session is already gone).
 */
export const SessionExpiringDialog: React.FC<SessionExpiringDialogProps> = ({
  open,
  remainingMs,
  onStay,
  onLogout,
  staying = false,
}) => {
  const { t } = useTranslation();
  const countdown = formatRemaining(remainingMs);

  return (
    <BaseDialog
      open={open}
      title={t('session-expiring.title', { defaultValue: 'Session Expiring Soon' })}
      maxWidth="xs"
      // Force an explicit choice — no backdrop/escape dismissal.
      disableBackdropClose
      actions={
        <>
          <Button onClick={onLogout} color="inherit" disabled={staying}>
            {t('session-expiring.logout', { defaultValue: 'Log out' })}
          </Button>
          <Button
            onClick={onStay}
            variant="contained"
            autoFocus
            disabled={staying}
            startIcon={staying ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('session-expiring.stay', { defaultValue: 'Stay signed in' })}
          </Button>
        </>
      }
    >
      <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7 }}>
        {t('session-expiring.message', {
          defaultValue:
            "You've been inactive for a while. Your session will expire soon — stay signed in to continue.",
        })}
      </Typography>
      {countdown && (
        <Typography
          variant="h5"
          sx={{ mt: 1.5, fontVariantNumeric: 'tabular-nums', textAlign: 'center' }}
          aria-live="polite"
        >
          {countdown}
        </Typography>
      )}
    </BaseDialog>
  );
};

export default SessionExpiringDialog;
